// Rankings: season ranqueada (+ histórico), nível, missões concluídas e dias seguidos.
import { api, errorMessage, type LeaderboardKind } from "../api";
import { loadSprites, spriteHtml } from "../sprites";
import type { View } from "../router";
import { esc, screen } from "../ui";
import { lang, t, type MsgKey } from "../i18n";

const TIERS = [
  ["pokeball", "ball"], ["greatball", "ball ball--great"], ["ultraball", "ball ball--ultra"], ["masterball", "ball ball--master"],
] as const;
const ROMAN = ["", "I", "II", "III", "IV"];

/** "Great Ball II · 40 pts" no idioma atual. */
function rankLabel(r: { tier: string; division?: number; points?: number; games?: number }): string {
  if (r.division === undefined || r.points === undefined) return t("ranking.unranked");
  const name = t(`rank.${r.tier}` as MsgKey);
  return r.tier === "masterball" ? `${name} · ${t("ranking.pts", { n: r.points })}` : `${name} ${ROMAN[r.division] ?? ""} · ${t("ranking.pts", { n: r.points })}`;
}

const TABS = ["ranked", "level", "missions", "streak"] as const;
type Tab = (typeof TABS)[number];

function boardValue(kind: LeaderboardKind, v: number, extra: number): string {
  if (kind === "level") return t("ranking.levelValue", { n: v });
  if (kind === "missions") return t("ranking.missionsValue", { n: v });
  return `${t("ranking.streakValue", { n: v })}${extra > v ? ` <span class="bw-dim small">· ${t("ranking.bestStreak", { n: extra })}</span>` : ""}`;
}

async function renderBoard(body: HTMLElement, kind: LeaderboardKind) {
  const [r] = await Promise.all([api.leaderboard(kind), loadSprites()]);
  body.innerHTML = `
    <section class="bw-panel bw-stack">
      <div class="bw-panel__header">${t(`ranking.tab.${kind}` as MsgKey)}</div>
      <p style="margin:0" class="bw-dim">${t(`ranking.about.${kind}` as MsgKey)}</p>
      <p style="margin:0"><b>${t("ranking.you")}</b> ${r.me ? boardValue(kind, r.me.value, r.me.extra) : "—"}${r.me?.position ? ` · #${r.me.position}` : ""}</p>
      <table class="ladder"><thead><tr><th>#</th><th>${t("ranking.trainer")}</th><th style="text-align:right">${t(`ranking.col.${kind}` as MsgKey)}</th></tr></thead>
      <tbody>${r.top.length ? r.top.map((p) => `<tr class="${p.me ? "me" : ""}"><td>${p.position}</td><td class="ladder-name">${spriteHtml(p.avatar ?? 25, 28)} ${esc(p.nickname)}</td><td>${boardValue(kind, p.value, p.extra)}</td></tr>`).join("") : `<tr><td colspan="3">${t("ranking.empty")}</td></tr>`}</tbody></table>
    </section>`;
}

export const rankingView: View = (root, { query }) => {
  let tab: Tab = (TABS as readonly string[]).includes(query.get("aba") ?? "") ? (query.get("aba") as Tab) : "ranked";
  root.innerHTML = screen(t("ranking.title"), "/menu", `
    <div class="bw-tabs" role="tablist">${TABS.map((k) => `<button class="bw-btn" role="tab" data-tab="${k}">${t(`ranking.tab.${k}` as MsgKey)}</button>`).join("")}</div>
    <div data-board><div class="bw-textbox">${t("common.loading")}</div></div>`);
  const body = root.querySelector<HTMLElement>("[data-board]")!;
  const show = () => {
    root.querySelectorAll<HTMLElement>("[data-tab]").forEach((b) => {
      b.classList.toggle("bw-btn--blue", b.dataset.tab === tab);
      b.setAttribute("aria-selected", String(b.dataset.tab === tab));
    });
    body.innerHTML = `<div class="bw-textbox">${t("common.loading")}</div>`;
    if (tab === "ranked") renderRanked(body);
    else renderBoard(body, tab).catch((err) => (body.innerHTML = `<div class="bw-alert bw-alert--error">${esc(errorMessage(err))}</div>`));
  };
  root.querySelector(".bw-tabs")!.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-tab]");
    if (!b) return;
    tab = b.dataset.tab as Tab;
    history.replaceState(null, "", `#/ranking${tab === "ranked" ? "" : `?aba=${tab}`}`);
    show();
  });
  show();
};

function renderRanked(body: HTMLElement) {
  Promise.all([api.ranking(), api.history()])
    .then(([r, h]) => {
      if (!r.season) {
        body.innerHTML = `<div class="bw-textbox">${t("ranking.noSeason")}</div>`;
        return;
      }
      const days = Math.max(0, Math.ceil((new Date(r.season.endsAt).getTime() - Date.now()) / 86400000));
      const me = r.me;
      const mode = (m: string) => t(m === "ranked" ? "ranking.mode.ranked" : m === "custom" ? "ranking.mode.custom" : "ranking.mode.normal");
      body.innerHTML = `
        <div class="tiers">${TIERS.map(([k, cls]) => `<div class="${me?.tier === k ? "current" : ""}"><i class="${cls}"></i>${t(`rank.${k}` as MsgKey)}</div>`).join("")}</div>
        <section class="bw-panel bw-stack">
          <div class="bw-panel__header">${esc(r.season.name)} · ${t("ranking.endsIn", { n: days })}</div>
          <p style="margin:0"><b>${t("ranking.you")}</b> ${esc(me && me.games > 0 ? rankLabel(me) : t("ranking.unranked"))}${me?.position ? ` · #${me.position}` : ""}${me && (me.placement_games_left ?? 0) > 0 ? ` · ${t("ranking.placement", { n: me.placement_games_left ?? 0 })}` : ""}</p>
          <table class="ladder"><thead><tr><th>#</th><th>${t("ranking.trainer")}</th><th class="hide-sm">${t("ranking.games")}</th><th style="text-align:right">${t("ranking.elo")}</th></tr></thead>
          <tbody>${r.top.length ? r.top.map((p) => `<tr><td>${p.position}</td><td>${esc(p.nickname)}</td><td class="hide-sm">${t("ranking.gamesWins", { n: p.games, w: p.wins })}</td><td>${esc(rankLabel(p))}</td></tr>`).join("") : `<tr><td colspan="4">${t("ranking.empty")}</td></tr>`}</tbody></table>
        </section>
        <section class="bw-panel">
          <div class="bw-panel__header">${t("ranking.history")}</div>
          <table class="ladder"><tbody>${h.matches.length ? h.matches.map((m) => `<tr><td>${t("ranking.placeOf", { p: m.placement, n: m.max_players })}</td><td>${mode(m.mode)}</td><td class="hide-sm">${new Date(m.ended_at).toLocaleString(lang())}</td><td>${m.points_delta !== null ? t("ranking.pts", { n: `${m.points_delta > 0 ? "+" : ""}${m.points_delta}` }) : ""}</td></tr>`).join("") : `<tr><td>${t("ranking.noMatches")}</td></tr>`}</tbody></table>
        </section>`;
    })
    .catch((err) => (body.innerHTML = `<div class="bw-alert bw-alert--error">${esc(errorMessage(err))}</div>`));
}
